import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  computed,
  inject,
  signal
} from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AuthSessionService } from '../auth/auth-session.service';
import { APP_ROUTE_PATHS } from '../core/routing/app-route-paths';
import { ConnectionFacade, ConnectionStatus } from '../core/state/connection.store';
import { DriversFacade } from '../core/state/drivers.store';
import { hasNoFilterSelection } from '../core/state/reference-data.derived';
import { ReferenceDataFacade } from '../core/state/reference-data.store';
import { SessionFacade } from '../core/state/session.store';
import { AudioNotificationService } from '../realtime/audio-notification.service';
import { LegacyPreferenceStorageService } from '../realtime/legacy-preference-storage.service';
import { NavigationLink } from '../shared-models/navigation-link.model';
import { MapOverlayPreferencesService } from '../shared-integrations/map-overlay-preferences.service';
import { ShellPreferencesService } from './shell-preferences.service';
import { ThemeService } from './theme.service';

@Component({
  selector: 'wm-app-shell',
  imports: [RouterLink, RouterLinkActive, RouterOutlet],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppShellComponent {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authSessionService = inject(AuthSessionService);
  private readonly sessionFacade = inject(SessionFacade);
  private readonly connectionFacade = inject(ConnectionFacade);
  private readonly driversFacade = inject(DriversFacade);
  private readonly referenceDataFacade = inject(ReferenceDataFacade);
  private readonly mapOverlayPreferences = inject(MapOverlayPreferencesService);
  private readonly audioNotificationService = inject(AudioNotificationService);
  private readonly preferenceStorage = inject(LegacyPreferenceStorageService);
  private readonly shellPreferencesService = inject(ShellPreferencesService);
  private readonly themeService = inject(ThemeService);

  protected readonly homePath = `/${APP_ROUTE_PATHS.operator}`;
  protected readonly brandLabel = 'ggMonitor';
  protected readonly accountLabel = computed(
    () => this.sessionFacade.accountName() ?? this.sessionFacade.operatorLabel() ?? 'ggAdmin'
  );
  protected readonly logoPath = './img/logo.png';
  protected readonly navigationLinks = signal<NavigationLink[]>([
    {
      iconClass: 'fa fa-map-marker',
      label: 'Monitoring',
      path: `/${APP_ROUTE_PATHS.operator}`
    },
    {
      iconClass: 'fa fa-car',
      label: 'Shuttle',
      path: `/${APP_ROUTE_PATHS.shuttle}`
    },
    {
      iconClass: 'fa fa-question-circle',
      label: 'Instructions',
      path: '/instructions'
    }
  ]);
  protected readonly connection = this.connectionFacade.connection;
  protected readonly connectionStatus = this.connectionFacade.status;
  protected readonly connectionLabel = computed(() => formatConnectionStatus(this.connectionStatus()));
  protected readonly connectionDebugLine = computed(() => {
    const connection = this.connection();
    const parts = [
      connection.debug.socketUrl ? `socket=${connection.debug.socketUrl}` : null,
      connection.debug.engineProtocol ? connection.debug.engineProtocol : null,
      connection.debug.lastSocketEvent ? `event=${connection.debug.lastSocketEvent}` : null,
      `retries=${connection.reconnectAttemptCount}`,
      connection.lastError ? `error=${connection.lastError}` : null
    ];

    return parts.filter((part): part is string => !!part).join(' | ');
  });
  protected readonly driverCounts = this.driversFacade.counts;
  // The counts follow the map's Regions dropdown; say which regions they are for when one is picked.
  protected readonly driverCountsScope = computed(() => {
    const selectedIds = this.mapOverlayPreferences.selectedRegionIds();

    if (selectedIds.length === 0 || hasNoFilterSelection(selectedIds)) {
      return '';
    }

    const lookup = this.referenceDataFacade.regionLookup();
    const names = selectedIds
      .map((regionId) => lookup[regionId]?.name)
      .filter((name): name is string => !!name);

    return names.length > 0 ? ` in ${names.join(', ')}` : '';
  });
  protected readonly totalDrivers = computed(() => {
    const counts = this.driverCounts();
    return counts.free + counts.busy;
  });
  protected readonly notificationEnabled = this.shellPreferencesService.notificationEnabled;
  protected readonly cancelNotificationEnabled =
    this.shellPreferencesService.cancelNotificationEnabled;
  protected readonly navigationOpen = signal(false);
  protected readonly headerMenuOpen = signal(false);
  protected readonly accountMenuOpen = signal(false);
  protected readonly fullscreenSupported = signal(false);
  protected readonly fullscreenActive = signal(false);
  protected readonly theme = this.themeService.theme;
  private readonly audioPrimed = signal(false);

  constructor() {
    this.fullscreenSupported.set(
      !!this.document?.documentElement?.requestFullscreen && !!this.document?.exitFullscreen
    );

    const handleFullscreenChange = () => {
      this.fullscreenActive.set(!!this.document?.fullscreenElement);
    };

    this.document?.addEventListener('fullscreenchange', handleFullscreenChange);
    this.destroyRef.onDestroy(() => {
      this.document?.removeEventListener('fullscreenchange', handleFullscreenChange);
    });
  }

  protected toggleNavigation(): void {
    this.navigationOpen.update((isOpen) => !isOpen);
  }

  protected closeNavigation(): void {
    this.navigationOpen.set(false);
  }

  protected toggleHeaderMenu(): void {
    this.headerMenuOpen.update((isOpen) => !isOpen);
  }

  protected toggleAccountMenu(event: Event): void {
    event.preventDefault();
    this.accountMenuOpen.update((isOpen) => !isOpen);
  }

  protected toggleNotification(): void {
    const nextValue = !this.notificationEnabled();
    this.shellPreferencesService.setNotificationEnabled(nextValue);

    if (nextValue) {
      this.primeAudio();
      this.audioNotificationService.previewNewOrderNotification();
    }
  }

  protected toggleCancelNotification(): void {
    const nextValue = !this.cancelNotificationEnabled();
    this.shellPreferencesService.setCancelNotificationEnabled(nextValue);

    if (nextValue) {
      this.primeAudio();
      this.audioNotificationService.previewCancelledOrderNotification();
    }
  }

  protected toggleTheme(): void {
    this.themeService.toggle();
  }

  protected async toggleFullscreen(): Promise<void> {
    if (!this.fullscreenSupported()) {
      return;
    }

    if (this.document.fullscreenElement) {
      await this.document.exitFullscreen();
      return;
    }

    await this.document.documentElement.requestFullscreen();
  }

  protected async toggleFullscreenFromLink(event: Event): Promise<void> {
    event.preventDefault();
    await this.toggleFullscreen();
  }

  protected async logout(): Promise<void> {
    this.accountMenuOpen.set(false);
    await this.authSessionService.logout();
  }

  protected async logoutFromLink(event: Event): Promise<void> {
    event.preventDefault();
    await this.logout();
  }

  @HostListener('document:pointerdown')
  protected primeAudioOnPointer(): void {
    this.primeAudio();
  }

  @HostListener('document:keydown')
  protected primeAudioOnKeydown(): void {
    this.primeAudio();
  }

  private primeAudio(): void {
    if (this.audioPrimed()) {
      return;
    }

    this.audioPrimed.set(true);
    this.audioNotificationService.primePlayback();
  }
}

function formatConnectionStatus(status: ConnectionStatus): string {
  switch (status) {
    case 'online':
      return 'ONLINE';
    case 'connecting':
      return 'CONNECTING';
    case 'reconnecting':
      return 'RECONNECTING';
    case 'error':
      return 'ERROR';
    case 'offline':
      return 'OFFLINE';
    case 'idle':
    default:
      return 'OFFLINE';
  }
}
